import { Card } from '@/components/sections/Card';
import { Bundle } from '@/services/api/contracts/catalog/bundles';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { useBundleProducts } from '@/services/bundles/useBundleProducts';
import { usePrintCurrencyStringForCurrentCustomer } from '@/utils/price';
import { FC, FunctionComponent, useMemo, useState } from 'react';
import { BundleActionsMenu } from './BundleActionsMenu';
import BundlePriceBreakdown from './BundlePriceBreakdown';
import BundleProductTableRow from './BundleProductTableRow';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { Picture } from '@/components/image/Image';
import { Product } from '@/services/api/contracts/catalog/types';

interface BundleProps {
  bundle: Bundle;
}

interface ProductImageProps {
  product: Product;
}

export const ProductImage: FC<ProductImageProps> = ({ product }) => {
  return product.imageUri?.id ? (
    <div className="w-full h-[150px] flex items-center justify-center">
      <Picture image={product.imageUri} containerHeight={150} containerWidth={175}></Picture>
    </div>
  ) : (
    <div className="w-full h-[150px] [&_svg]:w-[100%] [&_svg]:h-[100%]">
      {CategoryIcons[product.category.description]}
    </div>
  );
};

const displayProducts = (products: string[]): string => {
  if (products.length === 0) return '';
  if (products.length === 1) return products[0];

  const totalLength = products.reduce((acc, name) => acc + name.length, 0);
  if (totalLength <= 60) {
    return products.join(', ');
  }

  let currentLength = 0;
  const displayNames: string[] = [];

  for (const product of products) {
    if (currentLength + product.length <= 60) {
      displayNames.push(product);
      currentLength += product.length + 2; // Add 2 for the comma and space
    }
  }

  if (displayNames.length === 1) {
    const remainingProducts = products.length - 1;

    return `${products[0]} and ${remainingProducts} ${remainingProducts === 1 ? 'other' : 'others'}`;
  } else {
    return `${displayNames.join(', ')} and ${products.length - displayNames.length} others`;
  }
};

const BundleOverviewCard: FunctionComponent<BundleProps> = ({ bundle }) => {
  const { mainDevice, peripherals } = useBundleProducts(bundle.mainDevice, bundle.bundledItems);

  const allProducts = useMemo(() => {
    const products = [];
    if (mainDevice) products.push(mainDevice);
    products.push(...peripherals);
    return products;
  }, [mainDevice, peripherals]);

  const productNames = useMemo(() => {
    return displayProducts(allProducts.map(item => (item.count > 1 ? `${item.name} (x${item.count})` : item.name)));
  }, [allProducts]);

  const thumbnailProduct = mainDevice || allProducts[0];

  const totalPrice = useMemo(() => {
    const mainDevicePrice = (mainDevice?.price?.price ?? 0) * (mainDevice?.count ?? 1);
    const peripheralPrices = peripherals.reduce((total, currentItem) => {
      const count = bundle.bundledItems.find(item => item.catalogItem === currentItem.id)?.count;

      return total + (count ?? 1) * (currentItem.price?.price ?? 0);
    }, 0);

    return mainDevicePrice + peripheralPrices;
  }, [mainDevice, peripherals, bundle]);

  const printCurrencyString = usePrintCurrencyStringForCurrentCustomer();
  const cost = printCurrencyString(totalPrice);

  const [quickViewExpanded, setQuickViewExpanded] = useState(false);

  const role = useRole();

  return (
    <>
      <Card>
        <div className="flex flex-row space-x-5 w-full h-[150px] items-center">
          <div className="border-r border-secondary-std w-[200px] h-full flex items-center">
            {thumbnailProduct && <ProductImage product={thumbnailProduct} />}
          </div>
          <div className="flex-1 py-2">
            <p className="text-2xl font-semibold">{bundle.name}</p>
            <div className="mt-1">
              <span className="text-primary-500">{allProducts.length > 0 ? productNames : 'No products included'}</span>
            </div>
            <div className="text-2xl mt-3 text-primary-800 flex space-x-1 items-center">
              <span>{cost}</span>
            </div>
            {role.type !== RoleType.Company && (
              <BundlePriceBreakdown
                bundle={bundle}
                price={totalPrice}
                products={allProducts}
              />
            )}
          </div>
          <div>
            <BundleActionsMenu
              bundleId={bundle.id}
              showDetailCallback={(show: boolean) => setQuickViewExpanded(show)}
            />
          </div>
        </div>
        {quickViewExpanded && (
          <div className="flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden mt-4">
            <div className="flex flex-row items-center justify-between p-3 bg-gray-50 border-b border-gray-200 text-sm font-bold text-gray-700">
              <div className="flex-1">Product Name</div>
              <div className="ml-4 shrink-0 flex items-center gap-6">
                <div className="w-16 text-center">Quantity</div>
                <div className="w-24 text-right">Unit Price</div>
                <div className="w-24 text-right">Total Price</div>
              </div>
            </div>
            {allProducts.map(product => (
              <BundleProductTableRow
                key={`quick-view-${product.id}`}
                product={product}
                count={product.count}
              />
            ))}
          </div>
        )}
      </Card>
    </>
  );
};

export default BundleOverviewCard;
